import { Checkbox, Col, Row } from 'antd'
import React, { useEffect, useState } from 'react'
import format from 'utils/format'
import RefinancingButton from '../RefinancingButton/RefinancingButton'
import Modal from 'components/Modal/Modal'
import { ExclamationCircleFilled } from '@ant-design/icons'
import swal from 'utils/swal'
import RefinancingRepository from 'egi/repositories/RefinancingRepository'
import { IFinishedProposalsToRefinance, IRefinancingClient } from '../../refinancingInterfaces'
import SkeletonButton from 'antd/lib/skeleton/Button'
import { useDispatch } from 'react-redux'
import { simulationLastStep, simulationValues } from 'store/modules/simulation/actions'
import { convertToSimulationLastStep } from '../../refinancingFunctions'

interface ICheckboxItemProps {
  data: IFinishedProposalsToRefinance,
  selectedContract: IFinishedProposalsToRefinance | undefined,
  setSelectedContract: (id: IFinishedProposalsToRefinance | undefined) => void
}

function CheckboxItem ({ data, selectedContract, setSelectedContract }: ICheckboxItemProps) {
  const { proposalId, contractNumber, clientName, financing, deadline, installmentValue } = data
  const isChecked = selectedContract?.proposalId === proposalId

  const title = `${contractNumber} - ${clientName}`
  const financingFormatted = financing ? format.formatBRL(financing) : ''
  const installment = deadline && format.formatBRL(installmentValue)

  const handleChange = () => {
    setSelectedContract(isChecked ? undefined : data)
  }

  return (
    <Checkbox
      className='refinancing-checkbox-item'
      onChange={handleChange}
      checked={isChecked}
    >
      <div className="checkbox-content">
        <p className='title'>{title}</p>
        <p>Valor financiado: <b>{financingFormatted}</b></p>
        <p>Parcela: <b>{deadline}x de {installment}</b></p>
      </div>
    </Checkbox>
  )
}

interface IContractListProps {
  clientData: IRefinancingClient | undefined
  setRefinTitle: (title: string) => void
  callbackProductChooser: () => void
  selectedContract: IFinishedProposalsToRefinance | undefined
  setSelectedContract: (contract: IFinishedProposalsToRefinance | undefined) => void
}

export default function ContractList ({
  clientData,
  setRefinTitle,
  callbackProductChooser,
  selectedContract,
  setSelectedContract
}: IContractListProps) {
  const dispatch = useDispatch()
  const [visibleModal, setVisibleModal] = useState(false)
  const [showLoading, setShowLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [finishedProposalsToRefinance, setFinishedProposalsToRefinance] = useState<IFinishedProposalsToRefinance[]>([])
  const isContinueDisabled = selectedContract === undefined

  async function getFinishedProposalsToRefinance () {
    if (!clientData) return
    setShowLoading(true)
    setRefinTitle(clientData.name)

    try {
      const response = await RefinancingRepository.getFinishedProposalsToRefinance(clientData.cpf)
      const proposals = response.data.data?.proposals || []
      setFinishedProposalsToRefinance(proposals)
    } catch (err: any) {
      callbackProductChooser()
      swal.basic({ title: 'Atenção!', text: err.message, icon: 'warning' })
      console.error(err)
    } finally {
      setShowLoading(false)
    }
  }

  useEffect(() => {
    getFinishedProposalsToRefinance()
  }, [clientData])

  function closeModal () {
    setVisibleModal(false)
  }

  async function handleClick () {
    if (!clientData || !selectedContract) {
      swal.basic({ title: 'Atenção!', text: 'Dados do cliente ou contrato inválidos.', icon: 'warning' })
      return
    }
    setUpdateLoading(true)

    try {
      const response = await RefinancingRepository.getOutstandingBalance(clientData?.cpf, selectedContract?.contractNumber)
      const outstandingBalanceData = response.data.data?.outstandingBalance

      // Encontrou o contrato na consulta do saldo devedor?
      if (!outstandingBalanceData) {
        setVisibleModal(true)
        return
      }

      // Possui parcelas em atraso?
      if (outstandingBalanceData.overduePaymentCount > 0) {
        swal.basic({
          title: 'Existem parcelas em atraso',
          icon: 'warning',
          text: `O contrato selecionado possui ${outstandingBalanceData.overduePaymentCount} parcela(s) em atraso, totalizando ${format.formatBRL(outstandingBalanceData.amountOverduePayment)}. A primeira parcela em atraso venceu em ${outstandingBalanceData.dateOfFirstOverduePayment}. Para dar continuidade ao refinanciamento, é necessário regularizar essas pendências.`,
          confirmButtonText: 'Entendi',
          showCancelButton: false
        })
        return
      }

      const simulationResponse = await RefinancingRepository.createSimulationFromFinishedProposal(selectedContract.proposalId)
      const simulation = simulationResponse.data.data?.simulation

      // Saldo insuficiente? (valor do contrato - Saldo devedor) - Taxas = < R$ 50.000,00
      let amount
      if (simulation?.taxes && simulation?.liquid) {
        const totalTaxes = Object.values(simulation?.taxes).reduce((sum, value) => sum + value, 0)
        amount = (simulation?.liquid - outstandingBalanceData.amountToPay) - totalTaxes
      }

      setRefinTitle(`${selectedContract?.contractNumber} - ${selectedContract?.clientName}`)

      if (simulation) {
        dispatch(simulationValues({ ...simulation, outstandingBalance: outstandingBalanceData.amountToPay }, 1))

        const lastStep = convertToSimulationLastStep(simulation)
        if (amount) dispatch(simulationLastStep({ ...lastStep, liquid: amount }, 1))
      }
    } catch (err: any) {
      swal.basic({ title: 'Atenção!', text: err.message, icon: 'warning' })
      console.error(err)
    } finally {
      setUpdateLoading(false)
    }
  }

  function LoadingWrapper ({ loading, children }: { loading: boolean, children: React.ReactNode }) {
    if (loading) {
      return (
        <div className='contract-list-skeleton'>
          {new Array(4).fill('').map((_, idx) => (
            <Row gutter={[16, 16]} className='w-100' key={idx}>
              {new Array(3)
                .fill('')
                .map((_, idx) => (
                  <Col key={idx} xs={8} md={8} lg={8}>
                    <SkeletonButton className='w-100' />
                  </Col>
                ))
              }
            </Row>
          ))}
        </div>
      )
    }

    return <>{children}</>
  }

  return (
    <>
      <Modal
        className='contract-list-modal'
        title={
          <span className='modal-title'>
            <ExclamationCircleFilled style={{ fontSize: 20 }} />
            Contrato não existente na Prognum
          </span>
        }
        visible={visibleModal}
        cancelText={<></>}
        onCancel={closeModal}
        onOk={closeModal}
      >
        <div className="modal-content">
          <p>Entrar em contato com Rodobens por um de seus canais de atendimento:</p>

          <ul className='modal-info'>
            <li>0800 709 9220</li>
            <li>sacbanco@rodobens.com.br</li>
          </ul>
        </div>
      </Modal>

      <LoadingWrapper loading={showLoading} >
        <div className='refinancing-grid'>
          {finishedProposalsToRefinance.map((item) => (
            <CheckboxItem
              key={item.proposalId}
              data={item}
              selectedContract={selectedContract}
              setSelectedContract={setSelectedContract}
            />
          ))}
        </div>
      </LoadingWrapper>

      <RefinancingButton
        loading={updateLoading}
        isContinueDisabled={isContinueDisabled}
        handleClick={handleClick}
      />
    </>
  )
}
